import {
  AiPhotoConfigIdSchema,
  DEFAULT_OPENAI_BILLING,
  DEFAULT_STYLE,
  isAllowedExampleUrl,
  isOpenAiBilling,
  lookById,
  sceneById,
  type OpenAiBilling,
  type PublicAiPhotoCatalog,
  type StyleId,
} from '@ai-photo-style/shared';

export type EmbedVariant = 'stijlen' | 'scenes';

export interface EmbedConfig {
  /** Basis-URL van ai-photo-style-api, zonder slash aan het eind. */
  apiBase: string;
  /** Alleen in de testomgeving: gratis mock-API, gekozen met `#aps_api=mock`. */
  apiBaseMock?: string;
  turnstileSiteKey: string;
  /** Exact published tool configuration; absent preserves legacy variant selection. */
  configId?: string;
  /** Vierkante afbeelding op de knop. Alleen een gehoste voorbeeldfoto. */
  ctaImageUrl?: string;
  style: StyleId;
  /**
   * `stijlen` toont de vier fotostijlen. `scenes` toont de scènelijst.
   * Een stijl- of scène-id slaat de keuze over en start meteen die look.
   */
  variant: EmbedVariant;
  /** Gezet als `variant` een stijl- of scène-id is. Dan is er geen keuzestap. */
  fixedLook: string | null;
  /** Raw snippet value; custom fixed ids are validated only after catalog loading. */
  requestedVariant?: string;
  /** Resolved by boot; optional for older callers and offline fixtures. */
  catalog?: PublicAiPhotoCatalog;
  debug: boolean;
  /**
   * Alleen zinvol als de backend met PROVIDER=mock draait: `?aps_mock=fail|slow|timeout`
   * in de pagina-URL stuurt X-Mock-Scenario mee, zodat je foutpaden op je telefoon kunt testen.
   */
  mockScenario?: string;
  /** Alleen voor tests: `#aps_model=flux-2-pro` kiest een andere Replicate-preset (backend: ALLOW_MODEL_OVERRIDE). */
  modelOverride?: string;
  /**
   * Welke OpenAI-facturatie de API gebruikt (PROVIDER=openai).
   * `sparkles` = OPENAI_API_KEY; `hg` = OPENAI_API_KEY_HG (Hippe Geboortekaartjes).
   */
  openaiBilling: OpenAiBilling;
}

declare global {
  interface Window {
    AI_PHOTO_STYLE_CONFIG?: Partial<EmbedConfig>;
    dataLayer?: unknown[];
  }
}

/**
 * Config komt uit `window.AI_PHOTO_STYLE_CONFIG` (dev-adapter) of uit data-attributen
 * op de script-tag (Shop Asset: `<script src=... data-api-base=... data-turnstile-site-key=...>`).
 */
export function readConfig(script: HTMLScriptElement | null): EmbedConfig | null {
  const raw = window.AI_PHOTO_STYLE_CONFIG ?? {};
  const ds = script?.dataset ?? {};
  const apiBase = raw.apiBase ?? ds.apiBase;
  const turnstileSiteKey = raw.turnstileSiteKey ?? ds.turnstileSiteKey;
  if (!apiBase || !turnstileSiteKey) {
    console.warn('[ai-photo-style] config ontbreekt (apiBase/turnstileSiteKey), embed niet actief');
    return null;
  }
  const configId = raw.configId !== undefined ? raw.configId : ds.configId;
  if (configId !== undefined && !AiPhotoConfigIdSchema.safeParse(configId).success) {
    console.warn('[ai-photo-style] ongeldig configId, embed niet actief');
    return null;
  }
  // Ook via de hash (#aps_mock=fail), mocht de shop onbekende query-parameters herschrijven.
  const mock =
    new URLSearchParams(location.search).get('aps_mock') ??
    new URLSearchParams(location.hash.slice(1)).get('aps_mock') ??
    raw.mockScenario;
  const modelOverride =
    new URLSearchParams(location.search).get('aps_model') ?? new URLSearchParams(location.hash.slice(1)).get('aps_model');
  const wantMock =
    (new URLSearchParams(location.search).get('aps_api') ?? new URLSearchParams(location.hash.slice(1)).get('aps_api')) ===
    'mock';
  const base = wantMock && raw.apiBaseMock ? raw.apiBaseMock : apiBase;
  const requestedVariant = raw.variant ?? ds.variant;
  const tool = readTool(requestedVariant);
  return {
    apiBase: base.replace(/\/+$/, ''),
    turnstileSiteKey,
    ...(configId !== undefined ? { configId } : {}),
    ...(typeof raw.ctaImageUrl === 'string' && isAllowedExampleUrl(raw.ctaImageUrl) ? { ctaImageUrl: raw.ctaImageUrl } : {}),
    style: (raw.style ?? (ds.style as StyleId | undefined) ?? DEFAULT_STYLE) as StyleId,
    variant: tool.variant,
    fixedLook: tool.fixedLook,
    requestedVariant: typeof requestedVariant === 'string' && requestedVariant !== '' ? requestedVariant : undefined,
    ...(raw.catalog !== undefined ? { catalog: raw.catalog } : {}),
    debug: Boolean(raw.debug ?? ds.debug === 'true'),
    mockScenario: mock ?? undefined,
    modelOverride: modelOverride ?? undefined,
    openaiBilling: readOpenAiBilling(raw.openaiBilling ?? ds.openaiBilling),
  };
}

function readOpenAiBilling(value: unknown): OpenAiBilling {
  if (typeof value !== 'string') return DEFAULT_OPENAI_BILLING;
  const normalized = value.trim().toLowerCase();
  return isOpenAiBilling(normalized) ? normalized : DEFAULT_OPENAI_BILLING;
}

/** `stijlen`, `scenes`, of één stijl-/scène-id. Een onbekende waarde blijft de stijltool. */
function readTool(value: unknown): { variant: EmbedVariant; fixedLook: string | null } {
  if (value === 'scenes') return { variant: 'scenes', fixedLook: null };
  if (typeof value === 'string') {
    const look = lookById(value);
    if (look) return { variant: sceneById(look.id) ? 'scenes' : 'stijlen', fixedLook: look.id };
  }
  return { variant: 'stijlen', fixedLook: null };
}

let debugEnabled = false;

export function setDebug(on: boolean): void {
  debugEnabled = on;
}

export function log(...args: unknown[]): void {
  if (debugEnabled) console.info('[ai-photo-style]', ...args);
}
